import * as React from "react";

import { cn } from "@/lib/utils";

import { format, isValid, parse } from "date-fns";

import { Button } from "@/components/ui/Button";
import { Calendar } from "@/components/ui/Calendar";
import { Calendar as CalendarIcon } from "@/components/ui/Icons";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";

import { BodyText } from "../Text";

interface DatePickerProps {
  autoFocus?: boolean;
  date?: Date;
  setDate: (date: Date) => void;
  disablePast?: boolean;
  disableFuture?: boolean;
  label?: string;
  hint?: string | null;
  closeOnSelect?: boolean;
  dateFormat?: string;
  disabled?: boolean;
  allowManualInput?: boolean;
  inputClassName?: string;
}

export const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      autoFocus = false,
      date,
      setDate,
      disablePast = false,
      disableFuture = false,
      label = "Select Date",
      hint = null,
      closeOnSelect = false,
      dateFormat = "MM/dd/yyyy",
      disabled = false,
      allowManualInput = true,
      inputClassName,
    },
    ref,
  ) => {
    const [popoverOpen, setPopoverOpen] = React.useState(autoFocus);
    const [inputValue, setInputValue] = React.useState(
      date ? format(date, dateFormat) : "",
    );
    const [inputError, setInputError] = React.useState(false);

    // Update input value when date prop changes
    React.useEffect(() => {
      if (date) {
        setInputValue(format(date, dateFormat));
        setInputError(false);
      }
    }, [date, dateFormat]);

    const handleSelect = (dateIn: Date | undefined) => {
      if (dateIn) {
        setDate(dateIn);
        setInputValue(format(dateIn, dateFormat));
        setInputError(false);
      }

      if (closeOnSelect) {
        setPopoverOpen(false);
      }
    };

    const formatDateInput = (value: string): string => {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, "");

      // Auto-format with hyphens based on the standard MM-DD-YYYY format
      if (digitsOnly.length <= 2) {
        return digitsOnly;
      }
      if (digitsOnly.length <= 4) {
        return `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2)}`;
      }
      return `${digitsOnly.slice(0, 2)}-${digitsOnly.slice(2, 4)}-${digitsOnly.slice(4, 8)}`;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // Apply auto-formatting with hyphens
      const formattedValue = formatDateInput(value);
      setInputValue(formattedValue);

      // Don't try to parse incomplete dates
      if (formattedValue.length < 10) {
        // MM-DD-YYYY is 10 chars
        return;
      }

      try {
        // Convert the formatted input to the expected format for parsing
        const parsableValue = formattedValue.replace(/-/g, "/");
        const parsedDate = parse(
          parsableValue,
          dateFormat.replace(/-/g, "/"),
          new Date(),
        );

        if (isValid(parsedDate)) {
          setInputError(false);

          // Check if the date is within allowed range
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const isAllowed =
            (!disablePast || parsedDate >= today) &&
            (!disableFuture || parsedDate <= today);

          if (isAllowed) {
            setDate(parsedDate);
          } else {
            setInputError(true);
          }
        } else {
          setInputError(true);
        }
      } catch (error) {
        setInputError(true);
      }
    };

    const handleInputBlur = () => {
      // Reset to current date on blur if input is invalid
      if (inputError || !inputValue.trim()) {
        if (date) {
          setInputValue(format(date, dateFormat));
          setInputError(false);
        } else {
          setInputValue("");
        }
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        try {
          // Convert the formatted input to the expected format for parsing
          const parsableValue = inputValue.replace(/-/g, "/");
          const parsedDate = parse(
            parsableValue,
            dateFormat.replace(/-/g, "/"),
            new Date(),
          );

          if (isValid(parsedDate)) {
            // Check if the date is within allowed range
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const isAllowed =
              (!disablePast || parsedDate >= today) &&
              (!disableFuture || parsedDate <= today);

            if (isAllowed) {
              setDate(parsedDate);
              setInputError(false);
              if (closeOnSelect) {
                setPopoverOpen(false);
              }
            }
          }
        } catch (error) {
          // Keep the popover open if there's an error
        }
      }
    };

    return (
      <Popover onOpenChange={setPopoverOpen} open={popoverOpen}>
        <PopoverTrigger asChild>
          <div className={cn("w-full flex flex-col gap-1", inputClassName)}>
            {label && <Label>{label}</Label>}
            <Button
              ref={ref}
              disabled={disabled}
              variant="secondary"
              className={cn(
                "w-full min-w-[4rem] !justify-start text-primary text-left font-normal bg-white h-8",
                !date && "text-muted-foreground",
                popoverOpen ? "border-link" : "border-secondary",
              )}
              aria-label="Select date"
            >
              <span className="flex items-center">
                <CalendarIcon className="mr-1.5 h-3.5 w-4 stroke-black" />
                {date ? format(date, dateFormat) : null}
              </span>
            </Button>
            {hint && (
              <BodyText textColor="tertiary" size="xs">
                {hint}
              </BodyText>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 flex flex-col">
          {allowManualInput && (
            <div className="p-2 border-b">
              <Input
                type="text"
                placeholder="MM-DD-YYYY"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                className={cn(
                  "h-8",
                  inputError && "border-red-500 focus-visible:ring-red-500",
                )}
              />
            </div>
          )}
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            disablePast={disablePast}
            disableFuture={disableFuture}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    );
  },
);

DatePicker.displayName = "DatePicker";
